import { useAsyncRead } from './useAsyncRead';
import { useSession } from './useSession';
// hooks/useUser.ts — reactive hook for accessing user registry records.
//
// Components must NOT import usersService or the data layer directly.

import { useState, useEffect, useMemo } from 'react';
import type { User, SuggestedProfile } from '@/core/models';
import type { Post } from '@/core/models/post';
import { usersService } from '@/core/services/usersService';
import { useFollowedHandles } from './useFollow';
import { normHandle } from '@/core/stores/followStore';

/** Returns the User record for the given id (async-resolved on mount). */
export function useUser(id: string | null | undefined): User | null {
  const [user, setUser] = useState<User | null>(null);
  useEffect(() => {
    let alive = true;
    if (id) {
      void usersService.getUser(id).then((u) => {
        if (alive) setUser(u);
      });
    } else {
      // Schedule the null set asynchronously to avoid triggering the
      // react-hooks/set-state-in-effect rule on synchronous setState.
      const t = setTimeout(() => setUser(null), 0);
      return () => clearTimeout(t);
    }
    return () => {
      alive = false;
    };
  }, [id]);
  return user;
}

export interface UserByHandleHook {
  /**
   * The user, or null when it is not (yet) known. `usersService.getByHandle`
   * swallows a failed fetch to `null` internally (try/catch — see
   * usersService.ts) rather than rejecting, so from here a genuine 404 and a
   * network error are indistinguishable: both settle as `user: null` and
   * render as "not found". There is no error channel in this shape by design.
   */
  user: User | null;
  /** True until the by-handle lookup settles for the CURRENT handle. Render
   *  neither the profile nor "not found" while true — see
   *  ProfileByHandlePage.tsx for why this matters (the not-found flash). */
  loading: boolean;
}

/**
 * Returns the User record matched by handle, plus whether the lookup is
 * still in flight for that exact handle.
 *
 * `settledHandle` records which handle the async lookup last resolved for —
 * the same technique as useCommunities.ts's `useCommunity` (compare the
 * last-settled key against the current one rather than assume a fixed-latency
 * race, e.g. a 0 ms timer, so `loading` stays correct even when the lookup is
 * a real network fetch that can take arbitrarily long). This is NOT a full
 * mirror of `useCommunity`, though: `useCommunity`'s `loading` also requires
 * `!community` (a store cache-hit renders immediately even mid-refetch).
 * `useUserByHandle` has no such cache — `user` only ever comes from this
 * hook's own request — which is exactly what lets ProfileByHandlePage drop
 * its old stale-render guard: `user` is guaranteed current for `handle`
 * whenever `loading` is false. Adding a cache term here later would silently
 * reopen that guard's need — don't "fix" this omission without re-adding it.
 */
export function useUserByHandle(handle: string | null | undefined): UserByHandleHook {
  const [user, setUser] = useState<User | null>(null);
  const [settledHandle, setSettledHandle] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    if (handle) {
      void usersService
        .getByHandle(handle)
        .then((u) => {
          if (alive) setUser(u);
        })
        // .finally (not .then) so a rejected lookup still settles `loading` —
        // getByHandle swallows errors to null today (see the `user` doc above)
        // so this can't currently fire, but it keeps a future removal of that
        // try/catch from leaving the page stuck in the loading state forever.
        .finally(() => {
          if (alive) setSettledHandle(handle);
        });
    } else {
      // Schedule the null set asynchronously to avoid triggering the
      // react-hooks/set-state-in-effect rule on synchronous setState.
      const t = setTimeout(() => {
        setUser(null);
        setSettledHandle(null);
      }, 0);
      return () => clearTimeout(t);
    }
    return () => {
      alive = false;
    };
  }, [handle]);
  return { user, loading: !!handle && settledHandle !== handle };
}

export interface UserPostsHook {
  posts: Post[];
  loading: boolean;
}

/**
 * Returns a user's own posts (newest-first), async-resolved on mount.
 * Backed by the per-user API endpoint (/users/<id>/posts/) rather than a
 * client-side filter of the global feed — the feed only hydrates on Home, so
 * a directly-loaded profile would otherwise show an empty Posts tab.
 */
export function useUserPosts(id: string | null | undefined): UserPostsHook {
  const [state, setState] = useState<UserPostsHook>({ posts: [], loading: !!id });
  useEffect(() => {
    let alive = true;
    if (!id) {
      // Async null-set avoids the react-hooks set-state-in-effect rule.
      const t = setTimeout(() => setState({ posts: [], loading: false }), 0);
      return () => clearTimeout(t);
    }
    void usersService.getUserPosts(id).then((posts) => {
      if (alive) setState({ posts, loading: false });
    });
    return () => {
      alive = false;
    };
  }, [id]);
  return state;
}

/** Returns the suggested-profiles list, excluding accounts the user already follows. */
const emptySuggestions: SuggestedProfile[] = [];
const loadSuggestions = () => usersService.suggested();
export function useSuggestedProfiles() {
  const { currentUser } = useSession();
  const result = useAsyncRead(loadSuggestions, currentUser?.id ?? 'guest', emptySuggestions);
  const followed = useFollowedHandles();
  const profiles = useMemo(
    () => result.data.filter((p) => !followed.has(normHandle(p.handle))),
    [result.data, followed],
  );
  return Object.assign([...profiles], {
    loading: result.loading,
    error: result.error,
    retry: result.retry,
  });
}
